import authService from '../utils/authService';
import config from '../config';

class AgentApiService {
  constructor() {
    this.baseUrl = `${config.API_BASE_URL}/api/agent/dashboard`;
    
    // Explicitly bind all methods to ensure 'this' context is preserved
    this.makeApiCall = this.makeApiCall.bind(this);
    this.getDashboard = this.getDashboard.bind(this);
    this.getSearchHotel = this.getSearchHotel.bind(this);
    this.getSearchHistory = this.getSearchHistory.bind(this);
    this.getAllSearchHistory = this.getAllSearchHistory.bind(this);
    this.getPaginatedSearchHistory = this.getPaginatedSearchHistory.bind(this);
    this.getRecentSearchHistory = this.getRecentSearchHistory.bind(this);
    this.getSearchHistoryStats = this.getSearchHistoryStats.bind(this);
    this.getBookingDetails = this.getBookingDetails.bind(this);
    this.getBookingDetailsByStatus = this.getBookingDetailsByStatus.bind(this);
    this.getBookingHistory = this.getBookingHistory.bind(this);
    this.getBookingStatuses = this.getBookingStatuses.bind(this);
    this.getTokenRecharge = this.getTokenRecharge.bind(this);
    this.getBookingDetails = this.getBookingDetails.bind(this);
    this.getBookingDetailsByStatus = this.getBookingDetailsByStatus.bind(this);
    this.getBookingHistory = this.getBookingHistory.bind(this);
    this.getBookingStatuses = this.getBookingStatuses.bind(this);
    this.debugBookingDetails = this.debugBookingDetails.bind(this);
    this.getCustomerBookingDetail = this.getCustomerBookingDetail.bind(this);
    this.getBookingCancellation = this.getBookingCancellation.bind(this);
    this.getBookingHistory = this.getBookingHistory.bind(this);
    this.getPaymentInfo = this.getPaymentInfo.bind(this);
    this.getManualPayment = this.getManualPayment.bind(this);
    this.getSupportTicket = this.getSupportTicket.bind(this);
    this.getTokenHistory = this.getTokenHistory.bind(this);
    this.searchHotels = this.searchHotels.bind(this);
    this.rechargeTokens = this.rechargeTokens.bind(this);
    this.cancelBooking = this.cancelBooking.bind(this);
    this.generateVoucher = this.generateVoucher.bind(this);
    this.generateBookingPDF = this.generateBookingPDF.bind(this);
    this.createSupportTicket = this.createSupportTicket.bind(this);
    this.getBookingCancellations = this.getBookingCancellations.bind(this);
    this.getCancellationDetail = this.getCancellationDetail.bind(this);
    this.getCancellationStats = this.getCancellationStats.bind(this);
    this.getPaymentRefundDetails = this.getPaymentRefundDetails.bind(this);
    this.getPaymentRefundStats = this.getPaymentRefundStats.bind(this);
    this.getManualPaymentTickets = this.getManualPaymentTickets.bind(this);
    this.createDepositTicket = this.createDepositTicket.bind(this);
    this.getManualPaymentStats = this.getManualPaymentStats.bind(this);
    this.getSupportTickets = this.getSupportTickets.bind(this);
    this.getSupportTicketDetails = this.getSupportTicketDetails.bind(this);
    this.createSupportTicket = this.createSupportTicket.bind(this);
    this.addCommentToTicket = this.addCommentToTicket.bind(this);
    this.getSupportTicketStats = this.getSupportTicketStats.bind(this);
    this.getTokenRechargeDetails = this.getTokenRechargeDetails.bind(this);
    this.processTokenRecharge = this.processTokenRecharge.bind(this);
    this.getTokenRechargeStats = this.getTokenRechargeStats.bind(this);
    this.getTokenRechargeHistory = this.getTokenRechargeHistory.bind(this);
    this.getAllTokenRechargeHistory = this.getAllTokenRechargeHistory.bind(this);
    this.getTokenRechargeHistoryStats = this.getTokenRechargeHistoryStats.bind(this);
    this.getHotelDetails = this.getHotelDetails.bind(this);
    this.getHotelImages = this.getHotelImages.bind(this);
    this.getCustomerDetail = this.getCustomerDetail.bind(this);
    this.submitCustomerDetails = this.submitCustomerDetails.bind(this);
    this.getAllPassengerDetails = this.getAllPassengerDetails.bind(this);
    this.confirmBooking = this.confirmBooking.bind(this);
    
    console.log('🚀 AgentApiService initialized with explicit method binding');
    console.log('🔧 getCustomerDetail bound:', typeof this.getCustomerDetail);
  }

  // Helper method to make authenticated API calls
  async makeApiCall(endpoint, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeader()
      },
      credentials: 'include',
      ...options
    };

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, defaultOptions);
      
      if (response.status === 401) {
        // Token expired or invalid
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Dashboard APIs
  async getDashboard() {
    return this.makeApiCall('/');
  }

  async getSearchHotel() {
    return this.makeApiCall('/searchhotel');
  }

  async getSearchHistory() {
    return this.makeApiCall('/searchhistory');
  }

  // New dedicated search history API endpoints
  async getAllSearchHistory() {
    const endpoint = '/api/agent/search-history';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Search history API call failed:`, error);
      throw error;
    }
  }

  async getPaginatedSearchHistory(page = 0, size = 10, sortBy = 'searchDate', sortDir = 'desc') {
    const endpoint = `/api/agent/search-history/paginated?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Paginated search history API call failed:`, error);
      throw error;
    }
  }

  async getRecentSearchHistory() {
    const endpoint = '/api/agent/search-history/recent';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Recent search history API call failed:`, error);
      throw error;
    }
  }

  async getSearchHistoryStats() {
    const endpoint = '/api/agent/search-history/stats';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Search history stats API call failed:`, error);
      throw error;
    }
  }

  async getTokenRecharge() {
    return this.makeApiCall('/tokenrecharge');
  }

  // Booking Details APIs
  async getBookingDetails() {
    const endpoint = '/api/agent/booking-details';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Booking details API call failed:`, error);
      throw error;
    }
  }

  async getBookingDetailsByStatus(statuses = []) {
    const statusParams = statuses.length > 0 ? statuses.map(status => `status=${encodeURIComponent(status)}`).join('&') : '';
    const endpoint = `/api/agent/booking-details/filter-by-status${statusParams ? '?' + statusParams : ''}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Booking details filter API call failed:`, error);
      throw error;
    }
  }

  async getBookingHistory() {
    const endpoint = '/api/agent/booking-details/history';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Booking history API call failed:`, error);
      throw error;
    }
  }

  async getBookingStatuses() {
    const endpoint = '/api/agent/booking-details/statuses';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Booking statuses API call failed:`, error);
      throw error;
    }
  }

  async debugBookingDetails() {
    const endpoint = '/api/agent/booking-details/debug';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log('🔍 Debug booking details response:', data);
      return data;
    } catch (error) {
      console.error(`Debug booking details API call failed:`, error);
      throw error;
    }
  }

  async getCustomerBookingDetail(bookingId) {
    const endpoint = `/api/agent/booking-details/${bookingId}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Customer booking detail API call failed:`, error);
      throw error;
    }
  }

  async cancelBooking(bookingId) {
    const endpoint = `/api/agent/booking-details/${bookingId}/cancel`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Cancel booking API call failed:`, error);
      throw error;
    }
  }

  async generateVoucher(bookingId) {
    const endpoint = `/api/agent/booking-details/${bookingId}/voucher`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Generate voucher API call failed:`, error);
      throw error;
    }
  }

  async generateBookingPDF(bookingId, formData = {}) {
    const endpoint = `/api/agent/booking-details/${bookingId}/generate-pdf`;
    
    try {
      // Create form data for the request
      const formDataParams = new URLSearchParams();
      Object.keys(formData).forEach(key => {
        formDataParams.append(key, formData[key]);
      });

      const response = await fetch(`${config.API_BASE_URL}${endpoint}?${formDataParams}`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle PDF response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `booking-${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true, message: 'PDF downloaded successfully' };
    } catch (error) {
      console.error(`Generate PDF API call failed:`, error);
      throw error;
    }
  }

  async createSupportTicket(bookingId, ticketData) {
    const endpoint = `/api/agent/booking-details/${bookingId}/create-ticket`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(ticketData)
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Create support ticket API call failed:`, error);
      throw error;
    }
  }

  // Booking Cancellation APIs
  async getBookingCancellations(page = 0, size = 10, sortBy = 'cancellationDate', sortDir = 'desc') {
    const endpoint = `/api/agent/booking-cancellation?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Booking cancellations API call failed:`, error);
      throw error;
    }
  }

  async getCancellationDetail(cancellationId) {
    const endpoint = `/api/agent/booking-cancellation/${cancellationId}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Cancellation detail API call failed:`, error);
      throw error;
    }
  }

  async getCancellationStats() {
    const endpoint = '/api/agent/booking-cancellation/stats';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Cancellation stats API call failed:`, error);
      throw error;
    }
  }

  async getBookingCancellation() {
    return this.makeApiCall('/bookingcancellation');
  }

  async getPaymentInfo() {
    return this.makeApiCall('/paymentinfo');
  }

  async getManualPayment() {
    return this.makeApiCall('/manualpayment');
  }

  // Payment Refund APIs
  async getPaymentRefundDetails(page = 0, size = 10, sortBy = 'transactionDate', sortDir = 'desc') {
    const endpoint = `/api/agent/payment-refund?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Payment refund details API call failed:`, error);
      throw error;
    }
  }

  async getPaymentRefundStats() {
    const endpoint = '/api/agent/payment-refund/stats';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Payment refund stats API call failed:`, error);
      throw error;
    }
  }

  async getSupportTicket() {
    return this.makeApiCall('/supportticket');
  }

  async getTokenHistory() {
    return this.makeApiCall('/tokenhistory');
  }

  // POST APIs for actions
  async searchHotels(searchData) {
    return this.makeApiCall('/searchhotel', {
      method: 'POST',
      body: JSON.stringify(searchData)
    });
  }

  async rechargeTokens(rechargeData) {
    return this.makeApiCall('/tokenrecharge', {
      method: 'POST',
      body: JSON.stringify(rechargeData)
    });
  }

  // Hotel Details APIs
  async getHotelDetails(hotelId, provider, searchId) {
    const endpoint = `/api/hotel-details/${hotelId}?provider=${encodeURIComponent(provider)}&searchId=${encodeURIComponent(searchId)}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Hotel details API call failed:`, error);
      throw error;
    }
  }

  async getHotelImages(hotelId, provider) {
    const endpoint = `/api/hotel-details/${hotelId}/images?provider=${encodeURIComponent(provider)}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Hotel images API call failed:`, error);
      throw error;
    }
  }

  // Prebook APIs
  async getCustomerDetail(queryParams) {
    const endpoint = `/api/agent/prebook/customer-detail?${queryParams}`;
    
    try {
      // Create AbortController for 30-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds
      
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        signal: controller.signal
      });

      // Clear timeout if request completes
      clearTimeout(timeoutId);

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Customer detail API call timed out after 30 seconds');
        throw new Error('Prebook is not responding. Please try again later.');
      }
      console.error(`Customer detail API call failed:`, error);
      throw error;
    }
  }

  async submitCustomerDetails(passengerDetails, reserveCode, roomBookingCode, timerKey) {
    const endpoint = `/api/agent/prebook/customer-detail-submit?reserveCode=${encodeURIComponent(reserveCode)}&roomBookingCode=${encodeURIComponent(roomBookingCode)}&timerKey=${encodeURIComponent(timerKey)}`;
    
    try {
      // Create AbortController for 30-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds
      
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(passengerDetails),
        signal: controller.signal
      });

      // Clear timeout if request completes
      clearTimeout(timeoutId);

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Submit customer details API call timed out after 30 seconds');
        throw new Error('Prebook is not responding. Please try again later.');
      }
      console.error(`Submit customer details API call failed:`, error);
      throw error;
    }
  }

  async getAllPassengerDetails(reserveCode, roomBookingCode, timerKey) {
    const endpoint = `/api/agent/prebook/passenger-details?reserveCode=${encodeURIComponent(reserveCode)}&roomBookingCode=${encodeURIComponent(roomBookingCode)}&timerKey=${encodeURIComponent(timerKey)}`;
    
    try {
      // Create AbortController for 30-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds
      
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        signal: controller.signal
      });

      // Clear timeout if request completes
      clearTimeout(timeoutId);

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Get all passenger details API call timed out after 30 seconds');
        throw new Error('Prebook is not responding. Please try again later.');
      }
      console.error(`Get all passenger details API call failed:`, error);
      throw error;
    }
  }

  async confirmBooking(reserveCode, roomBookingCode, isHoldBooking, roomAllocations, bookingDetails) {
    const endpoint = `/api/agent/prebook/confirm-booking?reserveCode=${encodeURIComponent(reserveCode)}&roomBookingCode=${encodeURIComponent(roomBookingCode)}&isHoldBooking=${encodeURIComponent(isHoldBooking)}&roomAllocations=${encodeURIComponent(roomAllocations)}`;
    
    try {
      // Create AbortController for 30-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds
      
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(bookingDetails),
        signal: controller.signal
      });

      // Clear timeout if request completes
      clearTimeout(timeoutId);

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Confirm booking API call timed out after 30 seconds');
        throw new Error('Prebook is not responding. Please try again later.');
      }
      console.error(`Confirm booking API call failed:`, error);
      throw error;
    }
  }

  // Manual Payment APIs
  async getManualPaymentTickets(page = 0, size = 10, sortBy = 'transactionDate', sortDir = 'desc') {
    const endpoint = `/api/agent/manual-payment?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Manual payment tickets API call failed:`, error);
      throw error;
    }
  }

  async createDepositTicket(ticketData) {
    const endpoint = '/api/agent/manual-payment/create-ticket';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(ticketData)
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Create deposit ticket API call failed:`, error);
      throw error;
    }
  }

  async getManualPaymentStats() {
    const endpoint = '/api/agent/manual-payment/stats';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Manual payment stats API call failed:`, error);
      throw error;
    }
  }

  // Support Ticket APIs
  async getSupportTickets(page = 0, size = 10, sortBy = 'createdDate', sortDir = 'desc') {
    const endpoint = `/api/agent/support-ticket?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Support tickets API call failed:`, error);
      throw error;
    }
  }

  async getSupportTicketDetails(ticketId) {
    const endpoint = `/api/agent/support-ticket/${ticketId}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Support ticket details API call failed:`, error);
      throw error;
    }
  }

  async createSupportTicket(ticketData) {
    const endpoint = '/api/agent/support-ticket/create';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(ticketData)
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Create support ticket API call failed:`, error);
      throw error;
    }
  }

  async addCommentToTicket(ticketId, commentData) {
    const endpoint = `/api/agent/support-ticket/${ticketId}/comment`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(commentData)
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Add comment to ticket API call failed:`, error);
      throw error;
    }
  }

  async getSupportTicketStats() {
    const endpoint = '/api/agent/support-ticket/stats';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Support ticket stats API call failed:`, error);
      throw error;
    }
  }

  // Token Recharge APIs
  async getTokenRechargeDetails() {
    const endpoint = '/api/agent/token-recharge';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Token recharge details API call failed:`, error);
      throw error;
    }
  }

  async processTokenRecharge(rechargeData) {
    const endpoint = '/api/agent/token-recharge/recharge';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(rechargeData)
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Process token recharge API call failed:`, error);
      throw error;
    }
  }

  async getTokenRechargeStats() {
    const endpoint = '/api/agent/token-recharge/stats';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Token recharge stats API call failed:`, error);
      throw error;
    }
  }

  // Token Recharge History APIs
  async getTokenRechargeHistory(page = 0, size = 10, sortBy = 'rechargedDate', sortDir = 'desc') {
    const endpoint = `/api/agent/token-recharge-history?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Token recharge history API call failed:`, error);
      throw error;
    }
  }

  async getAllTokenRechargeHistory() {
    const endpoint = '/api/agent/token-recharge-history/all';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`All token recharge history API call failed:`, error);
      throw error;
    }
  }

  async getTokenRechargeHistoryStats() {
    const endpoint = '/api/agent/token-recharge-history/stats';
    
    try {
      const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`Token recharge history stats API call failed:`, error);
      throw error;
    }
  }
}

// Create the service instance
const agentApiServiceInstance = new AgentApiService();

// Verify all methods are properly bound
console.log('🧪 Final verification - getCustomerDetail:', typeof agentApiServiceInstance.getCustomerDetail);
console.log('🧪 Final verification - submitCustomerDetails:', typeof agentApiServiceInstance.submitCustomerDetails);
console.log('🧪 Final verification - getAllPassengerDetails:', typeof agentApiServiceInstance.getAllPassengerDetails);
console.log('🧪 Final verification - confirmBooking:', typeof agentApiServiceInstance.confirmBooking);

// Test that methods can be called (without actually calling them)
if (typeof agentApiServiceInstance.getCustomerDetail === 'function') {
  console.log('✅ getCustomerDetail is callable');
} else {
  console.error('❌ getCustomerDetail is NOT callable');
}

export default agentApiServiceInstance;